import React, { Suspense } from "react"
import { RouterProvider, useNavigate } from "react-router-dom"
import router from "@/router/config"
import { useDispatch } from "react-redux"
import { useEffectOnce, useLocalStorage, useReadLocalStorage } from "usehooks-ts"
import { Worker } from "@react-pdf-viewer/core"
import { useHotkeys } from "react-hotkeys-hook"
import { withAITracking } from "@microsoft/applicationinsights-react-js"
import {
    setAnswerGenerationPromptTemplate,
    setAnswerStream,
    setAssistantType,
    setCitationsHighlightMode,
    setCognitiveSearchQueryGenerationPromptTemplate,
    setDocsPreselectSearchThreshold,
    setDocsSearchThreshold,
    setExcludeCategory,
    setImproveSearchQueryMode,
    setModel,
    setTokensUsageMode,
    setUserType,
    setModelProvider,
    setPdfCitationDisplayMode,
    setPreselectContexFilesMode,
    setSearchApproach,
    setSelectedDocuments,
    setShowWelcomeMessage,
    setTemperatureCount,
    setUseSuggestFollowupQuestions,
    setUseReasoning,
} from "@/slices/chatSlice"
import {
    AssistantTypes,
    ImproveSearchQueryModes,
    ModelVersions,
    PreselectContextFilesModes,
    SearchApproaches,
    TokensUsageModes,
    UserTypes,
} from "@/api"

import "react-toastify/dist/ReactToastify.css"
import { toast, ToastContainer } from "react-toastify"

import { CssBaseline, ThemeProvider } from "@mui/material"
import theme from "./theme"
import { ModalProvider } from "@/components/ModalProvider"
import axios from "axios"
import { AuthenticatedTemplate, UnauthenticatedTemplate } from "@azure/msal-react"
import unauthenticatedRouter from "@/router/unauthenticatedRouter"
import LoginSuccessListener from "@/components/LoginSuccessListener"
import { DownloadType } from "@/components/DownloadFromBlob/downloadFromBlob"
import { CitationsHighlightMode } from "@/components/CitationPDFViewer/CitationPDFViewer"
import { isMobile } from "@/utils/mobileDetect"
import { setWorkflowJSON } from "@/slices/workflowsSlice"
import { getProviderOptions } from "@/components/Settings/useSettings"
import { getValidAccessToken } from "@/utils/accessToken"
import { AuthAxiosRequestConfig, getAuthRecoveryToken } from "@/utils/authAxios"
import {
    createSessionExpiredError,
    handleExhaustedUnauthorized,
    recoverFromUnauthorized,
    setRequestAuthorization,
    shouldForceReauthOn401,
} from "@/utils/authSessionRecovery"
import ProactiveTokenRefresh from "@/components/ProactiveTokenRefresh/ProactiveTokenRefresh"
import "./i18n"
import { t } from "i18next"
import { withTranslation } from "react-i18next"
import { reactPlugin } from "@/telemetry/appInsights"
import { DEFAULT_USE_REASONING } from "@/utils/brand"

axios.interceptors.request.use(
    async config => {
        const recoveryToken = getAuthRecoveryToken(config)
        if (recoveryToken) {
            setRequestAuthorization(config, recoveryToken)
            return config
        }

        const accessToken = await getValidAccessToken()
        if (accessToken) {
            setRequestAuthorization(config, accessToken)
        }

        return config
    },
    error => Promise.reject(error)
)

const waitForAuthRedirect = (): Promise<never> => new Promise(() => {})

const rejectUnlessRedirectStarted = async (
    redirectStarted: boolean
): Promise<never> => {
    if (redirectStarted) {
        return waitForAuthRedirect()
    }
    return Promise.reject(createSessionExpiredError())
}

axios.interceptors.response.use(
    response => response,
    async error => {
        const originalRequest = error.config as AuthAxiosRequestConfig | undefined
        const currentPath = window.location.href.split("#")[1] || "/"
        const isUnauthorized =
            error?.response?.status === 401 &&
            originalRequest &&
            !["/terms-of-service", "/privacy-policy"].includes(currentPath)

        if (!isUnauthorized) {
            return Promise.reject(error)
        }

        const authOptions = { isMobileDevice: isMobile() }

        if (originalRequest._retry) {
            if (!shouldForceReauthOn401(error)) {
                return Promise.reject(error)
            }
            const redirectStarted = await handleExhaustedUnauthorized(authOptions)
            return rejectUnlessRedirectStarted(redirectStarted)
        }

        const recovery = await recoverFromUnauthorized(originalRequest, authOptions)

        if (recovery.action === "redirect") {
            return rejectUnlessRedirectStarted(recovery.redirectStarted)
        }

        try {
            return await recovery.request()
        } catch (retryError) {
            if (axios.isAxiosError(retryError) && retryError.response?.status === 401) {
                if (!shouldForceReauthOn401(retryError)) {
                    return Promise.reject(retryError)
                }
                const redirectStarted = await handleExhaustedUnauthorized(authOptions)
                return rejectUnlessRedirectStarted(redirectStarted)
            }
            return Promise.reject(retryError)
        }
    }
)

function App() {
    const dispatch = useDispatch()

    const localSettings = useReadLocalStorage("personalSettings")
    const workflowSettings = useReadLocalStorage("workflowSettings")
    const selectedDocs = useReadLocalStorage("selectedDocs")
    const showTokens = useReadLocalStorage("showTokens")
    const [isShowTokens, setShowTokens] = useLocalStorage("showTokens", showTokens || false)

    useHotkeys(
        "ctrl+shift+x",
        () => {
            toast.info(t("Show tokens: {{state}}", { state: !isShowTokens ? "ON" : "OFF" }), {
                position: "top-center",
                autoClose: 2000,
            })
            setShowTokens(!isShowTokens)
        },
        [setShowTokens, isShowTokens]
    )
    // TODO: remove local setup after testing
    useEffectOnce(() => {
        // @ts-ignore
        dispatch(setAnswerGenerationPromptTemplate(localSettings?.answerGenerationPromptTemplate || ""))
        // @ts-ignore
        dispatch(
            setCognitiveSearchQueryGenerationPromptTemplate(
                // @ts-ignore
                localSettings?.cognitiveSearchQueryGenerationPromptTemplate || ""
            )
        )
        // @ts-ignore
        dispatch(setExcludeCategory(localSettings?.useExcludeCategory || ""))
        // @ts-ignore
        dispatch(setUseSuggestFollowupQuestions(localSettings?.useSuggestFollowupQuestions || false))
        // @ts-ignore
        dispatch(
            // @ts-ignore
            setPreselectContexFilesMode(localSettings?.preselectContextFilesMode || PreselectContextFilesModes.Enabled)
        )
        // @ts-ignore
        const storedSearchApproach = localSettings?.searchApproach === "keyword" ? SearchApproaches.Auto : localSettings?.searchApproach
        // @ts-ignore
        dispatch(setSearchApproach(storedSearchApproach || SearchApproaches.Auto))
        // @ts-ignore
        dispatch(setTemperatureCount(localSettings?.temperatureCount || 0.3))
        // @ts-ignore
        dispatch(setDocsPreselectSearchThreshold(localSettings?.docsPreselectSearchThreshold || 50))
        // @ts-ignore
        dispatch(setDocsSearchThreshold(localSettings?.docsSearchThreshold || 30))
        // @ts-ignore
        dispatch(setModel(localSettings?.selectedModel || ModelVersions.PRO))
        // @ts-ignore
        dispatch(setAssistantType(localSettings?.assistantType || AssistantTypes.DOC_ASSISTANT))
        dispatch(setSelectedDocuments(selectedDocs || []))
        // @ts-ignore
        dispatch(setShowWelcomeMessage(localSettings?.showWelcomeMessage || false))
        // @ts-ignore
        dispatch(setAnswerStream(localSettings?.answerStream || ""))
        // @ts-ignore
        // Welcome popup disabled — keep code but don't auto-enable for new users
        // if (localSettings?.showWelcomeMessage === undefined) {
        //     dispatch(setShowWelcomeMessage(true))
        // }

        // @ts-ignore
        dispatch(setPdfCitationDisplayMode(localSettings?.pdfCitationDisplayMode || DownloadType.Auto))

        dispatch(
            // @ts-ignore
            setCitationsHighlightMode(localSettings?.citationsHighlightMode || CitationsHighlightMode.SIMPLE_PRIORITY)
        )

        // @ts-ignore
        dispatch(setWorkflowJSON(workflowSettings?.workflowJSON || ""))

        const provider = getProviderOptions().find(p => p.key === "azure")?.key ?? ""
        // @ts-ignore
        dispatch(setModelProvider(localSettings?.modelProvider || provider || ""))
        // @ts-ignore
        dispatch(setImproveSearchQueryMode(localSettings?.improveSearchQueryMode || ImproveSearchQueryModes.Manual))
        // @ts-ignore
        dispatch(setTokensUsageMode(localSettings?.selectedTokensUsageMode || TokensUsageModes.Increased))
        // @ts-ignore
        dispatch(setUserType(localSettings?.userType || UserTypes.Expert))
        // @ts-expect-error localSettings type mismatch
        dispatch(setUseReasoning(localSettings?.useReasoning ?? DEFAULT_USE_REASONING))
    })

    return (
        <ThemeProvider theme={theme}>
            <ModalProvider>
                <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                    <CssBaseline />
                    <AuthenticatedTemplate>
                        <ProactiveTokenRefresh />
                        <RouterProvider router={router} />
                    </AuthenticatedTemplate>
                    <UnauthenticatedTemplate>
                        <Suspense fallback={<div style={{ minHeight: "100vh" }} />}>
                            <RouterProvider router={unauthenticatedRouter} />
                        </Suspense>
                    </UnauthenticatedTemplate>
                    <LoginSuccessListener />
                    <ToastContainer
                        position="top-right"
                        theme="light"
                        style={{
                            zIndex: 2000,
                        }}
                        draggable
                    />
                </Worker>
            </ModalProvider>
        </ThemeProvider>
    )
}

export default withAITracking(reactPlugin, withTranslation()(App))
